// src/components/admin/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  IconButton, Button, Chip, Skeleton, Alert
} from '@mui/material';
import {
  People, Message, TrendingUp, Token, Business,
  Assessment, History, Add, Visibility,
  Phone, Analytics
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import StatCard from '../common/StatCard';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const statsData = await adminApi.getAdminStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message || 'فشل في تحميل إحصائيات لوحة التحكم');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const QuickActionCard = ({ title, description, icon, color, onClick, disabled = false }) => (
    <Card
      sx={{
        height: '100%',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: disabled ? 'none' : 'translateY(-4px)',
          boxShadow: disabled ? 'none' : '0 8px 25px rgba(0,0,0,0.15)',
        },
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={disabled ? undefined : onClick}
    >
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          bgcolor: `${color}.50`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}>
          {icon}
        </Box>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="60%" height={32} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid key={item} xs={12} sm={6} md={3}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardStats}>
              إعادة المحاولة
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" fontWeight="700" gutterBottom>
            لوحة تحكم الإدارة
          </Typography>
          <Typography variant="body1" color="text.secondary">
            نظرة عامة على النظام وإدارة العملاء
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي العملاء"
              value={stats?.total_clients || 0}
              icon={<People />}
              color="primary"
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <StatCard
              title="المحادثات النشطة"
              value={stats?.active_conversations || 0}
              icon={<Message />}
              color="success"
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي الرسائل"
              value={stats?.total_messages_exchanged || 0}
              icon={<TrendingUp />}
              color="info"
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <StatCard
              title="التوكنز المستهلكة"
              value={stats?.total_tokens_consumed ? stats.total_tokens_consumed.toLocaleString() : '0'}
              icon={<Token />}
              color="warning"
            />
          </Grid>
        </Grid>

        {/* Today's Stats */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            إحصائيات اليوم
          </Typography>
                  <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="700" color="primary.main">
                {stats?.conversations_today || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                محادثات جديدة
              </Typography>
            </Box>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="700" color="success.main">
                {stats?.active_clients_today || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                عملاء نشطين
              </Typography>
            </Box>
          </Grid>
        </Grid>
        </Paper>

        {/* Top Clients by Tokens */}
        {stats?.top_clients_by_tokens && stats.top_clients_by_tokens.length > 0 && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              أفضل العملاء حسب استهلاك التوكنز
            </Typography>
            <Grid container spacing={2}>
              {stats.top_clients_by_tokens.map((client, index) => (
                <Grid key={client.id} xs={12} sm={6} md={4}>
                  <Card sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={`#${index + 1}`}
                        color={index === 0 ? 'warning' : index === 1 ? 'default' : 'primary'}
                        size="small"
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="600">
                          {client.company_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {client.total_tokens_consumed?.toLocaleString() || 0} توكن
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Quick Actions */}
        <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
          إجراءات سريعة
        </Typography>
        
        <Grid container spacing={3}>
          <Grid xs={12} sm={6} md={3}>
            <QuickActionCard
              title="إدارة العملاء"
              description="عرض وتعديل بيانات العملاء"
              icon={<Business color="primary" />}
              color="primary"
              onClick={() => navigate('/admin/clients')}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <QuickActionCard
              title="إضافة عميل جديد"
              description="إنشاء حساب عميل جديد"
              icon={<Add color="success" />}
              color="success"
              onClick={() => navigate('/admin/clients/new')}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <QuickActionCard
              title="الرسوم البيانية"
              description="عرض الإحصائيات والرسوم البيانية"
              icon={<Assessment color="info" />}
              color="info"
              onClick={() => navigate('/admin/charts')}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <QuickActionCard
              title="إدارة الأقسام"
              description="إدارة أقسام الدعم الفني وأرقام الهاتف"
              icon={<Phone color="warning" />}
              color="warning"
              onClick={() => navigate('/admin/departments')}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <QuickActionCard
              title="إحصائيات التحويل"
              description="مراقبة أداء نظام التحويل"
              icon={<Analytics color="info" />}
              color="info"
              onClick={() => navigate('/admin/transfer-stats')}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <QuickActionCard
              title="سجلات النظام"
              description="عرض سجلات النظام والأحداث"
              icon={<History color="secondary" />}
              color="secondary"
              onClick={() => navigate('/admin/logs')}
            />
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Paper sx={{ p: 3, mt: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            النشاط الأخير
          </Typography>
          <Typography variant="body2" color="text.secondary">
            سيتم إضافة النشاط الأخير هنا قريباً...
          </Typography>
        </Paper>
      </Container>
    </motion.div>
  );
}
